// Overlay editor: select, move, resize, create and delete field boxes on top of
// the rendered page. Field geometry always lives in PDF user space; screen
// pixels exist only for the duration of a gesture.

import { rectToScreen, screenToRect } from './viewer.js';

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const MIN_SCREEN_SIZE = 6;
const DRAG_THRESHOLD = 4;

let idCounter = 0;
export const nextFieldId = () => `fld_${++idCounter}`;

export class Editor {
  constructor({ onChange, onSelect }) {
    this.pages = [];
    this.selectedId = null;
    this.newFieldType = 'text';
    this.onChange = onChange || (() => {});
    this.onSelect = onSelect || (() => {});

    this._onKeyDown = this._onKeyDown.bind(this);
    document.addEventListener('keydown', this._onKeyDown);
  }

  setPages(pages) {
    this.pages = pages;
    this.selectedId = null;
    for (const page of pages) this._wirePage(page);
    this.redraw();
  }

  get allFields() {
    return this.pages.flatMap((p) => p.fields);
  }

  findField(id) {
    for (const page of this.pages) {
      const field = page.fields.find((f) => f.id === id);
      if (field) return { field, page };
    }
    return null;
  }

  select(id) {
    this.selectedId = id;
    this.redraw();
    this.onSelect(id ? this.findField(id) : null);
  }

  updateSelected(patch) {
    const found = this.findField(this.selectedId);
    if (!found) return;
    Object.assign(found.field, patch);
    this.redraw();
    this.onChange();
  }

  deleteSelected() {
    const found = this.findField(this.selectedId);
    if (!found) return;
    found.page.fields = found.page.fields.filter((f) => f.id !== this.selectedId);
    this.selectedId = null;
    this.redraw();
    this.onSelect(null);
    this.onChange();
  }

  clearAll() {
    for (const page of this.pages) page.fields = [];
    this.selectedId = null;
    this.redraw();
    this.onSelect(null);
    this.onChange();
  }

  /* ---------------- rendering ---------------- */

  redraw() {
    for (const page of this.pages) {
      page.overlay.replaceChildren(
        ...page.fields.map((field) => this._fieldElement(field, page)),
      );
    }
    this.onChange();
  }

  _fieldElement(field, page) {
    const el = document.createElement('div');
    el.className = `field field--${field.type}`;
    el.dataset.id = field.id;
    if (field.id === this.selectedId) el.classList.add('is-selected');

    const box = rectToScreen(field.rect, page.viewport);
    el.style.left = `${box.left}px`;
    el.style.top = `${box.top}px`;
    el.style.width = `${box.width}px`;
    el.style.height = `${box.height}px`;
    el.title = field.name;

    if (field.id === this.selectedId) {
      for (const dir of HANDLES) {
        const handle = document.createElement('span');
        handle.className = `handle handle--${dir}`;
        handle.dataset.handle = dir;
        el.append(handle);
      }
    } else {
      const tag = document.createElement('span');
      tag.className = 'field__tag';
      tag.textContent = field.name;
      el.append(tag);
    }
    return el;
  }

  /* ---------------- interaction ---------------- */

  _wirePage(page) {
    page.overlay.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      const handleEl = event.target.closest('.handle');
      const fieldEl = event.target.closest('.field');

      if (handleEl && fieldEl) {
        this._beginResize(event, page, fieldEl.dataset.id, handleEl.dataset.handle);
      } else if (fieldEl) {
        this._beginMove(event, page, fieldEl.dataset.id);
      } else {
        this._beginCreate(event, page);
      }
    });
  }

  _pointerPos(event, page) {
    const rect = page.overlay.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  _beginMove(event, page, id) {
    const found = this.findField(id);
    if (!found) return;
    this.select(id);

    const start = this._pointerPos(event, page);
    const origin = rectToScreen(found.field.rect, page.viewport);
    // select() rebuilt the overlay, so the element has to be looked up after it.
    const el = page.overlay.querySelector(`.field[data-id="${cssEscape(id)}"]`);

    this._gesture(
      event,
      page,
      (pos) => {
        const next = clampToOverlay(
          {
            left: origin.left + (pos.x - start.x),
            top: origin.top + (pos.y - start.y),
            width: origin.width,
            height: origin.height,
          },
          page.overlay,
        );
        found.field.rect = screenToRect(next, page.viewport);
        // Move the one element rather than rebuilding every field: a dense page
        // carries hundreds, and a full redraw per pointermove drops frames.
        applyScreenBox(el, next);
      },
      () => this.redraw(),
    );
  }

  _beginResize(event, page, id, dir) {
    const found = this.findField(id);
    if (!found) return;

    const start = this._pointerPos(event, page);
    const origin = rectToScreen(found.field.rect, page.viewport);
    const el = page.overlay.querySelector(`.field[data-id="${cssEscape(id)}"]`);

    this._gesture(event, page, (pos) => {
      const dx = pos.x - start.x;
      const dy = pos.y - start.y;
      let { left, top, width, height } = origin;

      if (dir.includes('w')) {
        left = origin.left + dx;
        width = origin.width - dx;
      }
      if (dir.includes('e')) width = origin.width + dx;
      if (dir.includes('n')) {
        top = origin.top + dy;
        height = origin.height - dy;
      }
      if (dir.includes('s')) height = origin.height + dy;

      // Flipping past an edge should not invert the box.
      if (width < MIN_SCREEN_SIZE) {
        if (dir.includes('w')) left = origin.left + origin.width - MIN_SCREEN_SIZE;
        width = MIN_SCREEN_SIZE;
      }
      if (height < MIN_SCREEN_SIZE) {
        if (dir.includes('n')) top = origin.top + origin.height - MIN_SCREEN_SIZE;
        height = MIN_SCREEN_SIZE;
      }

      const next = clampToOverlay({ left, top, width, height }, page.overlay);
      found.field.rect = screenToRect(next, page.viewport);
      applyScreenBox(el, next);
    }, () => this.redraw());
  }

  _beginCreate(event, page) {
    const start = this._pointerPos(event, page);
    const preview = document.createElement('div');
    preview.className = 'field field--preview';
    let dragged = false;

    this._gesture(
      event,
      page,
      (pos) => {
        const box = normalize(start, pos);
        if (box.width < DRAG_THRESHOLD && box.height < DRAG_THRESHOLD) return;
        if (!dragged) {
          dragged = true;
          page.overlay.append(preview);
        }
        Object.assign(preview.style, {
          left: `${box.left}px`,
          top: `${box.top}px`,
          width: `${box.width}px`,
          height: `${box.height}px`,
        });
      },
      (pos) => {
        preview.remove();
        if (!dragged) {
          this.select(null);
          return;
        }
        let box = clampToOverlay(normalize(start, pos), page.overlay);
        if (this.newFieldType === 'checkbox') {
          const side = Math.max(MIN_SCREEN_SIZE, Math.min(box.width, box.height));
          box = { ...box, width: side, height: side };
        }
        const field = {
          id: nextFieldId(),
          type: this.newFieldType,
          name: uniqueName(this.newFieldType, this.allFields),
          label: '',
          multiline: false,
          rect: screenToRect(box, page.viewport),
        };
        page.fields.push(field);
        this.select(field.id);
        this.onChange();
      },
    );
  }

  /** Shared pointer capture loop for move/resize/create. */
  _gesture(event, page, onMove, onEnd) {
    event.preventDefault();
    const target = page.overlay;
    target.setPointerCapture(event.pointerId);

    const move = (e) => onMove(this._pointerPos(e, page));
    const up = (e) => {
      target.releasePointerCapture(event.pointerId);
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
      target.removeEventListener('pointercancel', up);
      if (onEnd) onEnd(this._pointerPos(e, page));
      this.onChange();
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
    target.addEventListener('pointercancel', up);
  }

  _onKeyDown(event) {
    if (!this.selectedId) return;
    if (isTextEntry(event.target)) return;

    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      this.deleteSelected();
      return;
    }
    if (event.key === 'Escape') {
      this.select(null);
      return;
    }

    const nudge = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] }[
      event.key
    ];
    if (nudge) {
      event.preventDefault();
      const step = event.shiftKey ? 10 : 1;
      const found = this.findField(this.selectedId);
      found.field.rect = {
        ...found.field.rect,
        x: found.field.rect.x + nudge[0] * step,
        y: found.field.rect.y + nudge[1] * step,
      };
      this.redraw();
    }
  }
}

// Only guard shortcuts against controls where the keys mean something else.
// Radios and checkboxes keep focus after a click, so treating every <input> as
// text entry would silently disable Delete and the arrow keys.
const TEXT_ENTRY_TYPES = new Set([
  'text', 'search', 'url', 'tel', 'email', 'password', 'number', 'date',
  'datetime-local', 'month', 'week', 'time',
]);

function isTextEntry(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return tag === 'INPUT' && TEXT_ENTRY_TYPES.has(el.type);
}

function applyScreenBox(el, box) {
  if (!el) return;
  el.style.left = `${box.left}px`;
  el.style.top = `${box.top}px`;
  el.style.width = `${box.width}px`;
  el.style.height = `${box.height}px`;
}

/** Field ids are generated, but escaping keeps the selector safe regardless. */
function cssEscape(value) {
  return window.CSS?.escape ? window.CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
}

function normalize(a, b) {
  return {
    left: Math.min(a.x, b.x),
    top: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  };
}

function clampToOverlay(box, overlay) {
  const maxW = overlay.clientWidth;
  const maxH = overlay.clientHeight;
  const width = Math.min(box.width, maxW);
  const height = Math.min(box.height, maxH);
  return {
    left: Math.max(0, Math.min(box.left, maxW - width)),
    top: Math.max(0, Math.min(box.top, maxH - height)),
    width,
    height,
  };
}

function uniqueName(type, fields) {
  const base = type === 'checkbox' ? 'checkbox' : 'field';
  const used = new Set(fields.map((f) => f.name));
  let i = 1;
  while (used.has(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}
